import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, CalendarDays, Mail as MailIcon, Send, Globe2 } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import { api, adminHeaders, clearAdminToken, getAdminToken } from "@/lib/api";
import { useSettings } from "@/lib/SettingsContext";

const FIELDS = [
  {
    key: "calendly_url",
    label: "Calendly URL (Consultation)",
    icon: CalendarDays,
    placeholder: "https://calendly.com/your-handle/consultation",
    help:
      "Paste the full Calendly link to your consultation booking page. The Book a Consultation page will embed it automatically.",
  },
  {
    key: "speaking_calendly_url",
    label: "Calendly URL (Speaking) — optional",
    icon: CalendarDays,
    placeholder: "https://calendly.com/your-handle/speaking-fit-call",
    help:
      "Optional. If you have a separate Calendly link for speaking fit calls, paste it here.",
  },
  {
    key: "mailchimp_action_url",
    label: "Mailchimp Embedded Form Action URL",
    icon: Send,
    placeholder:
      "https://something.usX.list-manage.com/subscribe/post?u=...&id=...",
    help:
      "In Mailchimp, go to Audience > Signup forms > Embedded forms and copy the form action URL. While empty, signups still flow into the admin Inbox > Newsletter tab.",
  },
  {
    key: "public_contact_email",
    label: "Public contact email",
    icon: MailIcon,
    placeholder: "consult@wanakeetrask.com",
    help: "Shown in the footer and on the Book a Consultation page when scheduling is not set.",
  },
  {
    key: "instagram_url",
    label: "Instagram URL — optional",
    icon: Globe2,
    placeholder: "https://instagram.com/your-handle",
  },
  {
    key: "linkedin_url",
    label: "LinkedIn URL — optional",
    icon: Globe2,
    placeholder: "https://linkedin.com/in/your-handle",
  },
  {
    key: "facebook_url",
    label: "Facebook URL — optional",
    icon: Globe2,
    placeholder: "https://facebook.com/your-page",
  },
];

export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const { refresh } = useSettings();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!getAdminToken()) {
        navigate("/admin", { replace: true });
        return;
      }
      try {
        const { data } = await api.get("/admin/settings", { headers: adminHeaders() });
        setForm(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          clearAdminToken();
          navigate("/admin", { replace: true });
        } else {
          toast.error("Could not load settings.");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        FIELDS.map((f) => [f.key, (form[f.key] || "").trim()]),
      );
      const { data } = await api.patch("/admin/settings", payload, { headers: adminHeaders() });
      setForm(data);
      await refresh();
      toast.success("Settings saved. The public site reflects these immediately.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Settings">
      {loading ? (
        <div className="wt-card p-10 text-center wt-text-muted">Loading settings…</div>
      ) : (
        <form
          data-testid="admin-settings-form"
          onSubmit={save}
          className="max-w-2xl wt-card p-6 sm:p-8 space-y-6"
        >
          <div>
            <SectionEyebrow>Connect your tools</SectionEyebrow>
            <p className="mt-3 text-sm wt-text-muted">
              Paste links from your accounts here. The public site updates instantly. While
              fields are empty, the site uses graceful fallbacks so the experience never breaks.
            </p>
          </div>

          {FIELDS.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="flex flex-col gap-2">
                <label className="wt-label" htmlFor={f.key}>
                  <span className="inline-flex items-center gap-2">
                    <Icon size={14} style={{ color: "var(--wt-primary)" }} />
                    {f.label}
                  </span>
                </label>
                <input
                  id={f.key}
                  type="text"
                  data-testid={`admin-settings-${f.key}`}
                  className="wt-input"
                  placeholder={f.placeholder}
                  value={form[f.key] || ""}
                  onChange={set(f.key)}
                />
                {f.help && <p className="text-xs wt-text-muted">{f.help}</p>}
              </div>
            );
          })}

          <div className="pt-3 flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-settings-save"
              className="wt-btn-primary h-11 px-5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save settings"}
            </button>
            <p className="text-xs wt-text-muted">Changes are live the moment you save.</p>
          </div>
        </form>
      )}
    </AdminShell>
  );
}
